import { FunctionComponent } from "react";
import { Button, Card, Col, Empty, Form, Row, Tag, Typography } from "antd";
import {
    BookOutlined,
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    LeftOutlined,
    PlusOutlined,
    RightOutlined,
    SettingOutlined,
    TeamOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { ClasseViewProps } from "./types";
import { ClasseDtoF, JourActiviteEnum } from "../../../services/classe";
import { NiveauInterne } from "../../../services/inscription";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const JOUR_LABELS: Record<JourActiviteEnum, string> = {
    [JourActiviteEnum.SAMEDI_MATIN]: 'Sam. matin',
    [JourActiviteEnum.DIMANCHE_MATIN]: 'Dim. matin',
    [JourActiviteEnum.DIMANCHE_APRES_MIDI]: 'Dim. après-midi',
    [JourActiviteEnum.MERCREDI_APRES_MIDI]: 'Mer. après-midi',
};

const NIVEAU_COLOR: Partial<Record<NiveauInterne, string>> = {
    [NiveauInterne.P1]: 'purple',
    [NiveauInterne.P2]: 'purple',
    [NiveauInterne.N1_1]: 'blue',
    [NiveauInterne.N1_2]: 'blue',
    [NiveauInterne.N2_1]: 'cyan',
    [NiveauInterne.N2_2]: 'cyan',
    [NiveauInterne.N3_1]: 'geekblue',
    [NiveauInterne.N3_2]: 'geekblue',
    [NiveauInterne.N4_1]: 'volcano',
    [NiveauInterne.N4_2]: 'volcano',
    [NiveauInterne.DEBUTANT]: 'green',
    [NiveauInterne.INTERMEDIAIRE]: 'orange',
    [NiveauInterne.AVANCE]: 'red',
};

export const ClasseDesktopView: FunctionComponent<ClasseViewProps> = ({
    form,
    classes,
    debutAnneeScolaire,
    onCreateClasse,
    onModifierClasse,
    onDeleteClasse,
    doSearchClasses,
    handleAnneeScolaireChanged,
}) => {
    const currentAnnee: number = Form.useWatch('anneeDebut', form) ?? debutAnneeScolaire;

    const navigateYear = (delta: number) => {
        const newAnnee = currentAnnee + delta;
        form.setFieldValue('anneeDebut', newAnnee);
        handleAnneeScolaireChanged(newAnnee);
        doSearchClasses({ anneeDebut: newAnnee, anneeFin: newAnnee + 1 });
    };

    const totalEleves = classes.reduce((acc, c) => acc + (c.liensClasseEleves?.length ?? 0), 0);

    const getEnseignantsDisplay = (classe: ClasseDtoF): string => {
        if (!classe.enseignants || classe.enseignants.length === 0) return 'Non renseigné';
        return classe.enseignants.map(e => e.nomPrenom).join(', ');
    };

    const getClasseCard = (classe: ClasseDtoF) => {
        const nbEleves = classe.liensClasseEleves?.length ?? 0;
        const niveauColor = classe.niveau ? (NIVEAU_COLOR[classe.niveau] ?? 'default') : 'default';

        return (
            <Col xs={24} sm={12} lg={8} key={classe.id}>
                <Card
                    className="card-focus-effect mes-classes-card"
                    bordered
                    hoverable
                    title={
                        <div className="mes-classes-card-title-row">
                            <span style={{ fontWeight: 600, fontSize: 15 }}>{classe.libelle}</span>
                            {classe.niveau && (
                                <Tag color={niveauColor} className="mes-classes-niveau-tag">
                                    {classe.niveau}
                                </Tag>
                            )}
                        </div>
                    }
                >
                    <div className="mes-classes-card-info-row">
                        <UserOutlined className="mes-classes-card-info-icon" />
                        <Text className="mes-classes-card-info-text">{getEnseignantsDisplay(classe)}</Text>
                    </div>
                    <div className="mes-classes-card-info-row">
                        <TeamOutlined className="mes-classes-card-info-icon" />
                        <Text className="mes-classes-card-info-text">
                            <strong>{nbEleves}</strong>&nbsp;{nbEleves <= 1 ? 'élève inscrit' : 'élèves inscrits'}
                        </Text>
                    </div>
                    {classe.activites && classe.activites.length > 0 && (
                        <div className="mes-classes-card-activites">
                            <CalendarOutlined className="mes-classes-card-info-icon" style={{ marginTop: 3 }} />
                            <div className="mes-classes-activites-tags">
                                {classe.activites.map(a => (
                                    <Tag key={a.jour} color="blue">{JOUR_LABELS[a.jour] ?? a.jour}</Tag>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="mes-classes-card-footer admin-classes-card-actions">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => onModifierClasse(classe)}
                            style={{ flex: 1 }}
                        >
                            Modifier
                        </Button>
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => onDeleteClasse(classe)}
                        />
                    </div>
                </Card>
            </Col>
        );
    };

    return (
        <Form form={form} initialValues={{ anneeDebut: dayjs().year() }}>
            <Form.Item name="anneeDebut" hidden>
                <input type="hidden" />
            </Form.Item>

            <div className="mes-classes-page">
                {/* Hero — style AdminAdhesion, couleur admin orange */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '16px', paddingBottom: '16px', borderBottom: '3px solid #fa8c16', marginBottom: '0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '48px', height: '48px', borderRadius: '10px', backgroundColor: '#fa8c16', flexShrink: 0 }}>
                        <SettingOutlined style={{ fontSize: '24px', color: '#ffffff' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                            Administration des classes
                        </Title>
                        <Text type="secondary">Créez, modifiez et gérez les classes de l'année scolaire</Text>
                    </div>
                    <div className="mes-classes-year-nav">
                        <Button type="text" icon={<LeftOutlined />} onClick={() => navigateYear(-1)} className="mes-classes-year-btn" style={{ color: '#fa8c16' }} {...({ testid: "annee-precedente" } as any)} />
                        <span className="mes-classes-year-label" style={{ color: '#92400e' }}>
                            <CalendarOutlined style={{ marginRight: 8 }} />
                            Année scolaire&nbsp;<strong>{currentAnnee}–{currentAnnee + 1}</strong>
                        </span>
                        <Button type="text" icon={<RightOutlined />} onClick={() => navigateYear(1)} className="mes-classes-year-btn" style={{ color: '#fa8c16' }} {...({ testid: "annee-suivante" } as any)} />
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onCreateClasse}
                        style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', flexShrink: 0 }}
                    >
                        Nouvelle classe
                    </Button>
                </div>

                {/* Stats */}
                {classes.length > 0 && (
                    <div className="mes-classes-stats-bar">
                        <div className="mes-classes-stat-item">
                            <BookOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                            <span className="mes-classes-stat-value">{classes.length}</span>
                            <span className="mes-classes-stat-label">{classes.length <= 1 ? 'classe' : 'classes'}</span>
                        </div>
                        <div className="mes-classes-stat-divider" />
                        <div className="mes-classes-stat-item">
                            <TeamOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                            <span className="mes-classes-stat-value">{totalEleves}</span>
                            <span className="mes-classes-stat-label">{totalEleves <= 1 ? 'élève' : 'élèves'}</span>
                        </div>
                    </div>
                )}

                {/* Grille */}
                <div className="mes-classes-grid-container">
                    {classes.length === 0 ? (
                        <div className="mes-classes-empty">
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                    <span>
                                        Aucune classe pour l'année scolaire&nbsp;
                                        <strong>{currentAnnee}–{currentAnnee + 1}</strong>
                                    </span>
                                }
                            >
                                <div className="mes-classes-empty-actions">
                                    <Button onClick={() => navigateYear(-1)} icon={<LeftOutlined />}>Année précédente</Button>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={onCreateClasse}
                                        style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                                    >
                                        Créer une classe
                                    </Button>
                                    <Button onClick={() => navigateYear(1)} icon={<RightOutlined />}>Année suivante</Button>
                                </div>
                            </Empty>
                        </div>
                    ) : (
                        <Row gutter={[20, 20]}>
                            {classes.map(classe => getClasseCard(classe))}
                        </Row>
                    )}
                </div>
            </div>
        </Form>
    );
};
